import { useCallback, useEffect, useState } from 'react';
import {
  generateMockEvaluations,
  generateMockTraces,
  type LlmEvaluationData,
  type LlmTraceData,
} from './mock-llm-data';

// Simulate GraphQL query result structure
type LlmDataQueryResult = {
  traces: LlmTraceData[];
  evaluations: LlmEvaluationData[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

// Simulate network delay like a real GraphQL call
const simulateNetworkDelay = (ms: number = 800 + Math.random() * 1200) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

// Simulate occasional network errors (5% chance)
const simulateNetworkError = () => {
  if (Math.random() < 0.05) {
    throw new Error('Network error: Failed to fetch LLM data');
  }
};

export const useLlmData = (): LlmDataQueryResult => {
  const [traces, setTraces] = useState<LlmTraceData[]>([]);
  const [evaluations, setEvaluations] = useState<LlmEvaluationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Simulate network delay
      await simulateNetworkDelay();

      // Simulate potential network error
      simulateNetworkError();

      // Generate mock data (this would be replaced by actual GraphQL calls)
      const mockTraces = generateMockTraces();
      const mockEvaluations = generateMockEvaluations();

      setTraces(mockTraces);
      setEvaluations(mockEvaluations);
    } catch (err) {
      console.error('Failed to fetch LLM data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    traces,
    evaluations,
    loading,
    error,
    refetch,
  };
};

// Hook specifically for traces (mimics what would be useListLlmTracesQuery)
export const useLlmTraces = () => {
  const { traces, loading, error, refetch } = useLlmData();

  return {
    data: { listLlmTraces: traces },
    loading,
    error,
    refetch,
  };
};

// Hook specifically for evaluations (mimics what would be useListLlmEvaluationsQuery)
export const useLlmEvaluations = () => {
  const { evaluations, loading, error, refetch } = useLlmData();

  return {
    data: { listLlmEvaluations: evaluations },
    loading,
    error,
    refetch,
  };
};
